import { useEffect } from 'react';

import { BasicRoute, BasicRouteProps } from './BasicRoute';

import { useTenantByUuid } from '@utils/hooks/useTenantByUuid';
import { useUserModules } from '@utils/hooks/useUserModules';

import { AuthService } from '@services/AuthService/AuthService';
import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import { authVar, tenantModulesVar } from '@boot/reactiveVariables';

interface CompanyRouteProps
    extends Omit<BasicRouteProps, 'condition' | 'loading' | 'redirectTo'> {
    additionalCondition?: boolean;
    modules?: 'cases' | 'disclosures' | 'surveys' | 'subsidiarySites';
    redirectTo?: BasicRouteProps['redirectTo'];
}

export const CompanyRoute = ({
    additionalCondition,
    modules,
    redirectTo,
    ...routeProps
}: CompanyRouteProps) => {
    const tenantUuid = AuthService.getTenantUuid();
    const graphApi = AuthService.getGraphApi();
    const isLoggedIn = useReactiveVar(authVar).isLoggedIn;
    const tenantModules = useReactiveVar(tenantModulesVar);
    const { data: dataModules } = useUserModules();

    const currentModules = isLoggedIn
        ? dataModules?.user.tenant?.modules
        : tenantModules;

    const { data, loading } = useTenantByUuid({
        skip: !tenantUuid || !graphApi,
        variables: {
            uuid: tenantUuid ?? '',
        },
    });

    const hasId = !!data?.tenant.uuid;
    const condition =
        typeof additionalCondition == 'boolean' || !!modules
            ? hasId &&
              (additionalCondition ||
                  !!(modules && !!currentModules?.[modules]))
            : hasId;

    useEffect(() => {
        if (isLoggedIn && !tenantUuid) {
            console.error(
                'CompanyRoute: user is logged in but not tenantUuid found',
            );
            void AuthService.logout(true);
        }
    }, [isLoggedIn, tenantUuid]);

    return (
        <BasicRoute
            {...routeProps}
            condition={condition}
            loading={loading}
            redirectTo={redirectTo ?? getPath({ path: '/auth/welcome' })}
        />
    );
};
